import { createBrowserHistory, To, Action } from "history";
import * as React from "react";
import { createRoot, Root } from "react-dom/client";
import { match } from "path-to-regexp";
import Application from "../Application";
import { Layouts } from "../common/Constants";
import AppLayout from "../ui/layouts/AppLayout";
import RootLayout from "../ui/layouts/RootLayout";
import BaseLayout from "../ui/layouts/BaseLayout";
import { ITranslationService } from "../service/locale/TranslationService";
import CheckLoginStatusInteractor from "../interactor/authentication/CheckLoginStatusInteractor";
import { INotificationService } from "../service/notification/NotificationService";

export interface TRouteOptions {
  secure?: boolean;
  permissions?: string | string[];
  name?: string;
  layout?: string;
  initialProps?: any;
}
export interface TRoute {
  path: string;
  page: any;
  options?: TRouteOptions;
}

export interface IRouter {
  registerRoutes(...routes: TRoute[]): void;
  showLocation(
    locationDescription: { action: Action; location: To } | string
  ): Promise<any>;
  replace(location: To, state?: any): void;
  push(location: To, state?: any): void;
  bindLocationListener(): void;
  goBack(): void;
  location: TRoute;
}
const Router = (
  context,
  rootElement,
  application: Application,
  routes: TRoute[]
) => {
  const _history = createBrowserHistory({});
  let _routes = routes;
  let _currentRoute: TRoute;
  const _rootElement = rootElement;
  const _application = application;
  const _notificationService: INotificationService =
    _application.container.resolve<INotificationService>("notificationService");
  const basePath = import.meta.env.VITE_BASE_URL;
  let root: Root | undefined;

  function registerRoutes(...routeCollection: TRoute[]) {
    const registeredRoutes = routeCollection.map((route) => {
      route.path = `${basePath}${route.path}`;
      return route;
    });
    _routes = [..._routes, ...registeredRoutes];
  }

  function matchURI(path, uri, search = "", hash = "") {
    const matchFn = match(path, { decode: decodeURIComponent });
    const result = matchFn(uri);
    if (!result) {
      return null;
    }

    const params = { ...result.params };
    if (search) {
      const urlParams = new URLSearchParams(search);
      for (const [key, value] of urlParams.entries()) {
        params[key] = value;
      }
    }
    return params;
  }

  function push(location: To = {}, state?: any) {
    return _history.push(location, state);
  }

  function goBack() {
    return _history.back();
  }

  function replace(location: To, state?: any) {
    return _history.replace(location, state);
  }

  async function showLocation(
    locationDescription:
      | {
          action: Action;
          location: To;
        }
      | string
  ) {
    let _location: To;
    if (typeof locationDescription === "string") {
      _location = {
        pathname: locationDescription
      };
    } else {
      let location = locationDescription.location;
      if (typeof location === "string") {
        _location = {
          pathname: location
        };
      } else {
        _location = location;
      }
    }
    try {
      await resolve(_routes, _location);
    } catch (error: any) {
      if (error.status) {
        switch (error.status) {
          case 401:
            _notificationService.showError(error.message);
            return replace({
              ...context,
              pathname: `${basePath}/device-login`,
              search: `?redirect=${encodeURI(
                _location.pathname + (_location.search || "")
              )}`
            });
          case 404:
            return replace({
              pathname: `${basePath}/error`
            });
          default:
            _notificationService.showError(error.message);
        }
      }
    }
  }

  function registerLocationListener() {
    _history.listen(showLocation);
  }

  var _router = {
    registerRoutes,
    showLocation,
    replace,
    push,
    bindLocationListener: registerLocationListener,
    goBack,
    get location() {
      return _currentRoute;
    }
  } as IRouter;

  function buildPage(route: TRoute, props) {
    const _layoutType =
      (route.options && route.options.layout) || Layouts.DEFAULT;
    let layout: any;
    switch (_layoutType) {
      case Layouts.APP:
        layout = AppLayout;
        break;
      case Layouts.ROOT:
        layout = RootLayout;
        break;
      case Layouts.BASE:
        layout = BaseLayout;
        break;
      default:
        layout = AppLayout;
    }
    const pageProps = {
      ...props,
      ...{
        store: _application.store,
        router: _router,
        translate:
          _application.container.resolve<ITranslationService>(
            "translationService"
          ).get
      }
    };
    const page = React.createElement(
      layout,
      pageProps,
      React.createElement(
        React.Suspense,
        {
          fallback: React.createElement("div", {
            style: {
              minHeight: 750
            }
          })
        },
        React.createElement(route.page, pageProps)
      )
    );
    return page;
  }

  async function resolve(routes, context) {
    for (const route of routes as TRoute[]) {
      if (context.error) {
        console.error(context.error);
      }
      const uri = context.error ? "/error" : context.pathname;
      const params = matchURI(route.path, uri, context.search, context.hash);
      if (!params) {
        continue;
      }
      _currentRoute = route;
      if (route.options && route.options.secure) {
        const isLoggedIn = await _application.container
          .resolve<CheckLoginStatusInteractor>("checkLoginStatus")
          .execute();
        if (!isLoggedIn) {
          return replace({
            ...context,
            pathname: `${basePath}/device-login`,
            search: `?redirect=${encodeURI(
              context.pathname + (context.search || "")
            )}`
          });
        }
      }

      const node = _rootElement;
      const props = Object.assign(
        {},
        route.options || {},
        (route.options && route.options.initialProps) || {},
        {
          params: Object.assign({}, params, context.state || {})
        }
      ) as any;
      const page = buildPage(route, props);
      if (!root) {
        root = createRoot(node);
      }
      return root.render(page);
    }
    const error = new Error(
      'Route "' + context.pathname + '" not found'
    ) as any;
    error.status = 404;
    throw error;
  }

  return _router;
};

export { Router };
